import { useState, useEffect } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Coins, Loader2, Sparkles, ExternalLink, Copy, Check, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [5, 10, 20, 35, 50];

// Enter a dollar amount OR a credit amount — the other side calculates with the
// discrete tier bonus. Every provider uses the same server quote.
export function CustomTopUp({ method, venmoHandle, unavailable = false }) {
  const { user } = useAuth();
  const accent = method === "venmo" ? "#3D95CE" : "#fbbf24";
  const [driver, setDriver] = useState("usd"); // which field the user is typing in
  const [usdText, setUsdText] = useState("10");
  const [creditsText, setCreditsText] = useState("");
  const [quoted, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [busy, setBusy] = useState(false);
  const [payerHandle, setPayerHandle] = useState("");
  const [ticket, setTicket] = useState(null);
  const [copied, setCopied] = useState(false);

  const activeRaw = driver === "usd" ? usdText : creditsText;
  const source = `${driver}:${activeRaw}`;
  const quote = quoted?.source === source ? quoted : null;
  const minimumError = activeRaw !== "" && Number.isFinite(Number(activeRaw)) &&
    Number(activeRaw) < (driver === "usd" ? 5 : 2000)
      ? "Minimum credit purchase: $5 for 2,000 credits." : "";

  useEffect(() => {
    const num = Number(activeRaw);
    if (!Number.isFinite(num) || num <= 0 || minimumError) { setQuote(null); setLoadingQuote(false); return; }
    let active = true;
    const controller = new AbortController();
    setLoadingQuote(true);
    const id = setTimeout(async () => {
      try {
        const param = driver === "usd" ? `usd=${num}` : `credits=${Math.round(num)}`;
        const res = await api.get(`/payments/quote?${param}`, { signal: controller.signal });
        if (active) setQuote({ ...res.data, source });
      } catch { if (active) setQuote(null); }
      finally { if (active) setLoadingQuote(false); }
    }, 350);
    return () => { active = false; clearTimeout(id); controller.abort(); };
  }, [activeRaw, driver, source, minimumError]);

  const usdValue = driver === "usd" ? usdText : (quote ? quote.amount_usd.toFixed(2) : "");
  const creditsValue = driver === "credits" ? creditsText : (quote ? String(quote.credits) : "");

  const proceed = async () => {
    if (!user) { window.location.href = "/login"; return; }
    if (!quote || minimumError) { toast.error(minimumError || "Enter a dollar or credit amount first."); return; }
    setBusy(true);
    try {
      if (method !== "venmo") {
        const res = await api.post("/payments/checkout", { provider: method, usd_amount: quote.amount_usd, origin_url: window.location.origin });
        if (res.data?.url) window.location.href = res.data.url;
        else throw new Error("No checkout URL");
      } else {
        const res = await api.post("/payments/venmo/request", { usd_amount: quote.amount_usd, payer_handle: payerHandle });
        setTicket(res.data);
        toast.success("Request logged — now send the payment on Venmo.");
      }
    } catch (e) { toast.error(apiError(e, "Couldn't start the top-up")); setBusy(false); }
    if (method === "venmo") setBusy(false);
  };

  const copyNote = () => {
    navigator.clipboard.writeText(ticket.note);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  if (ticket) {
    return (
      <div data-testid="custom-venmo-ticket" className="rounded-xl border border-[#3D95CE]/30 bg-[#3D95CE]/[0.06] p-4 space-y-3">
        <div className="text-white font-semibold flex items-center gap-2">
          <Coins className="h-4 w-4 text-amber-300" /> {ticket.credits.toLocaleString()} credits · ${ticket.amount_usd.toFixed(2)}
        </div>
        <ol className="text-sm text-white/75 space-y-2 list-decimal list-inside">
          <li>Send <span className="text-white font-semibold">${ticket.amount_usd.toFixed(2)}</span> to <span className="text-[#3D95CE] font-semibold">@{ticket.handle}</span> on Venmo.</li>
          <li className="flex flex-wrap items-center gap-2">Include this note: <span className="font-mono text-amber-200 bg-black/30 rounded px-2 py-0.5">{ticket.note}</span>
            <button onClick={copyNote} className="text-white/50 hover:text-white">{copied ? <Check className="h-3.5 w-3.5 text-emerald-300" /> : <Copy className="h-3.5 w-3.5" />}</button>
          </li>
          <li>We review and add your credits — you'll get a notification.</li>
        </ol>
        <div className="flex flex-wrap gap-2">
          <a href={ticket.pay_url} target="_blank" rel="noreferrer"><Button className="bg-[#3D95CE] text-white hover:bg-[#338ac0]"><ExternalLink className="h-4 w-4 mr-1.5" /> Open Venmo to pay</Button></a>
          <Button variant="ghost" className="text-white/60" onClick={() => setTicket(null)}>New amount</Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="custom-topup">
      {/* presets */}
      <div className="flex flex-wrap gap-2 mb-3">
        {PRESETS.map((p) => (
          <button key={p} data-testid={`custom-preset-${p}`}
            onClick={() => { setDriver("usd"); setUsdText(String(p)); }}
            className="rounded-full border border-white/12 bg-white/[0.04] px-3 py-1 text-sm text-white/70 hover:text-white hover:border-white/30 transition-colors">
            ${p}
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-[1fr_auto_1fr] gap-3 items-end">
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">You pay (USD)</span>
          <div className="relative mt-1.5">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50 font-mono">$</span>
            <Input data-testid="custom-usd-input" inputMode="decimal" min="5" aria-invalid={!!minimumError && driver === "usd"} value={usdValue}
              onChange={(e) => { setDriver("usd"); setUsdText(e.target.value.replace(/[^0-9.]/g, "")); }}
              className="bg-[hsl(220_18%_12%)] border-white/10 h-11 pl-7 font-mono text-lg" placeholder="0.00" />
          </div>
        </label>
        <div className="hidden sm:flex items-center justify-center pb-3 text-white/30"><ArrowLeftRight className="h-4 w-4" /></div>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">You get (credits)</span>
          <div className="relative mt-1.5">
            <Coins className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-300" />
            <Input data-testid="custom-credits-input" inputMode="numeric" min="2000" aria-invalid={!!minimumError && driver === "credits"} value={creditsValue}
              onChange={(e) => { setDriver("credits"); setCreditsText(e.target.value.replace(/[^0-9]/g, "")); }}
              className="bg-[hsl(220_18%_12%)] border-white/10 h-11 pl-9 font-mono text-lg text-amber-200" placeholder="0" />
          </div>
        </label>
      </div>

      {/* quote / bonus breakdown */}
      {minimumError && <p data-testid="custom-minimum-error" role="alert" className="mt-3 text-sm text-amber-200">{minimumError}</p>}
      <div data-testid="custom-bonus" className="mt-3 rounded-xl border p-3 text-sm flex flex-wrap items-center justify-between gap-3"
        style={{ borderColor: `${accent}33`, background: `${accent}10` }}>
        {loadingQuote ? (
          <span className="text-white/50 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Calculating…</span>
        ) : quote ? (
          <>
            <div className="min-w-0">
              <div data-testid="custom-quoted-total" className="text-white">
                <span className="font-semibold">{quote.credits.toLocaleString()}</span> credits for <span className="font-semibold">${quote.amount_usd.toFixed(2)}</span>
              </div>
              <div data-testid="custom-quoted-breakdown" className="text-white/45 text-xs mt-0.5">
                {quote.base_credits.toLocaleString()} base
                {quote.bonus_credits > 0 && <span className="text-emerald-300"> + {quote.bonus_credits.toLocaleString()} bonus</span>}
              </div>
            </div>
            {quote.bonus_pct > 0 && (
              <span data-testid="custom-bonus-percent" className="inline-flex items-center gap-1 font-mono text-[11px] rounded-full px-2.5 py-1 shrink-0 bg-emerald-400/15 text-emerald-300 border border-emerald-400/30">
                <Sparkles className="h-3 w-3" /> +{Math.round(quote.bonus_pct * 100)}% bonus
              </span>
            )}
          </>
        ) : (
          <span className="text-white/40">Enter an amount to see your credits.</span>
        )}
      </div>
      <p data-testid="custom-bonus-rule" className="mt-2 text-[11px] text-white/50">{quote ? `Base: ${quote.base_credits_per_usd} credits / $1. Bonuses: ${quote.bonus_tiers.map(t => `$${t.min_usd}+ → ${Math.round(t.bonus_pct * 100)}%`).join(" · ")}. Minimum $${quote.min_usd}.` : "Bonuses apply to the full purchase amount; tiers do not stack. Minimum purchase $5."}</p>

      {method === "venmo" && (
        <Input data-testid="custom-venmo-handle" value={payerHandle} onChange={(e) => setPayerHandle(e.target.value)}
          placeholder="Your Venmo username (optional, helps us match it)"
          className="bg-[hsl(220_18%_12%)] border-white/10 h-11 mt-3" />
      )}

      <Button data-testid="custom-proceed" onClick={proceed} disabled={busy || loadingQuote || !quote || unavailable || !!minimumError}
        className="mt-4 w-full h-12 font-medium text-base disabled:opacity-40"
        style={{ background: accent, color: method === "venmo" ? "white" : "black" }}>
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : method !== "venmo"
          ? <>Pay ${quote ? quote.amount_usd.toFixed(2) : "0.00"} {method === "stripe" ? "by card" : "with crypto"}</>
          : <>Request ${quote ? quote.amount_usd.toFixed(2) : "0.00"} via Venmo</>}
      </Button>
      {method === "stripe" && <p data-testid="custom-powered-by" className="mt-2 text-[11px] text-white/50">Stripe card processing · Powered by Steele Industries</p>}
    </div>
  );
}
